/**
 * The diff preview shown before an import commits (PLAN §5): every import is
 * additive by default — existing progress the import doesn't touch survives
 * — with an explicit "replace" mode that instead makes the watched set match
 * the import exactly, dropping anything the import doesn't know about.
 */

import type { EntryId } from "@schema/catalog.ts";
import type { WatchRecord } from "@schema/persist.ts";

export type ImportMode = "additive" | "replace";

export interface ImportDiffRow {
  entryId: EntryId;
  title: string;
  before: WatchRecord | null;
  after: WatchRecord | null;
}

export interface ImportDiff {
  toAdd: ImportDiffRow[];
  toUpdate: ImportDiffRow[];
  /** Only ever non-empty in "replace" mode. */
  toRemove: ImportDiffRow[];
  unchangedCount: number;
}

function recordsEqual(a: WatchRecord, b: WatchRecord): boolean {
  return a.watchedAt === b.watchedAt && a.rating10 === b.rating10 && a.rewatch === b.rewatch;
}

const byTitle = (a: ImportDiffRow, b: ImportDiffRow) => a.title.localeCompare(b.title);

export function computeImportDiff(
  current: Record<EntryId, WatchRecord>,
  imported: Record<EntryId, WatchRecord>,
  mode: ImportMode,
  titleById: Record<EntryId, string>,
): ImportDiff {
  const toAdd: ImportDiffRow[] = [];
  const toUpdate: ImportDiffRow[] = [];
  let unchangedCount = 0;

  for (const [entryId, after] of Object.entries(imported)) {
    const before = current[entryId] ?? null;
    const title = titleById[entryId] ?? entryId;
    if (!before) toAdd.push({ entryId, title, before: null, after });
    else if (!recordsEqual(before, after)) toUpdate.push({ entryId, title, before, after });
    else unchangedCount++;
  }

  const toRemove: ImportDiffRow[] = [];
  if (mode === "replace") {
    for (const [entryId, before] of Object.entries(current)) {
      if (entryId in imported) continue;
      toRemove.push({ entryId, title: titleById[entryId] ?? entryId, before, after: null });
    }
  }

  return {
    toAdd: toAdd.sort(byTitle),
    toUpdate: toUpdate.sort(byTitle),
    toRemove: toRemove.sort(byTitle),
    unchangedCount,
  };
}
